/**
 * @file rawFunction.js
 * @description A collection of utility functions used throughout the application for various operations
 * including station management, status handling, and data conversion.
 */

import { Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";
import { stationMetaData } from "../utils/rawData";
import Cookies from "js-cookie";
import PermissionDenied from "../components/PermissionDenied/PermissionDenied";

/**
 * Converts a station name into a URL-friendly format
 * @param {string} name - The original station name
 * @returns {string} URL-friendly version of the name (lowercase, hyphenated, no special characters)
 */
export const createUrlFriendlyName = (name) => {
  return name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, ""); // Remove special characters
};

/**
 * Finds a station by either its ID or name
 * @param {string} identifier - Station ID or name to search for
 * @returns {Object|null} Station object if found, null otherwise
 */
export const findStationByNameOrId = (identifier) => {
  if (!identifier) return null;

  // Force lowercase for consistency
  const normalizedIdentifier = decodeURIComponent(identifier)
    .toLowerCase()
    .replace(/%20/g, "-");

  const station = stationMetaData.find(
    (station) =>
      station.id.toLowerCase() === normalizedIdentifier ||
      createUrlFriendlyName(station.name) ===
        createUrlFriendlyName(normalizedIdentifier)
  );
  return station || null;
};

/**
 * Returns the appropriate color for a given station status
 * @param {string} status - The status of the station
 * @returns {string} Hex color code representing the status
 */
export const getStatusColor = (status) => {
  switch (status) {
    case "Operational":
      return "#DFF2BF"; // Green
    case "Non Operational":
      return "#FFBABA"; // Red
    case "Warning":
      return "#FEEFB3"; // Yellow
    default:
      return "#E0E0E0"; // Grey for unknown statuses
  }
};

/**
 * Creates a status icon component with appropriate styling
 * @param {string} status - The status of the station
 * @returns {JSX.Element} A styled Box component containing the status icon
 */
export const getStatusIcon = (status) => {
  let icon;
  let color;

  switch (status) {
    case "Operational":
      icon = <CheckCircleIcon />;
      color = "#78a62b";
      break;
    case "Non-Operational":
      icon = <CancelIcon />;
      color = "#ad4242";
      break;
    case "Warning":
      icon = <WarningIcon />;
      color = "#f2d666";
      break;
    default:
      icon = null;
      color = "#E0E0E0";
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "2em",
        height: "2em",
        borderRadius: "50%",
        border: `2px solid ${color}`,
        color: color,
        marginRight: "0.5em",
      }}
    >
      {icon}
    </Box>
  );
};

/**
 * Converts wind direction angle to cardinal direction
 * @param {number} angle - Wind direction angle in degrees
 * @returns {string} Cardinal direction (e.g., "N", "NE", "E", etc.)
 */
export const getCardinalDirection = (angle) => {
  // Adjust angle to be within the range of 0 to 360
  if (angle < 0) {
    angle = 360 + angle;
  }
  const directions = [
    "N",
    "NNE",
    "NE",
    "ENE",
    "E",
    "ESE",
    "SE",
    "SSE",
    "S",
    "SSW",
    "SW",
    "WSW",
    "W",
    "WNW",
    "NW",
    "NNW",
  ];
  const index = Math.round(angle / 22.5) % 16;
  return directions[index];
};

/**
 * Converts speed from meters per second to kilometers per hour
 * @param {number} speedInMS - Speed in meters per second
 * @returns {number} Speed in kilometers per hour
 */
export const convertMSToKMH = (speedInMS) => speedInMS * 3.6;

/**
 * Returns the quality control flag description for a given QC value
 * @param {Object} element - The element containing QC value
 * @returns {string|null} Description of the QC flag or null if no QC value exists
 */
export const getQCFlag = (element) => {
  if (!element.qc_value && element.qc_value !== 0) return null;
  const flags = {
    "-10": "Suppressed",
    "-1": "Missing",
    0: "Error",
    10: "Doubtful",
    15: "Suspect/Warning",
    20: "Inconsistent",
    100: "Acceptable/Passed",
  };
  return flags[element.qc_value.toString()] || "Unknown";
};

/**
 * Retrieves metadata for a specific station
 * @param {string} stationID - The ID of the station
 * @returns {Object|null} Station metadata object or null if not found
 */
export const getStationMetadata = (stationID) => {
  return stationMetaData.find((station) => station.id === stationID) || null;
};

/**
 * Gets the saved return URL and clears it from storage
 * @returns {string|null} The return URL or null if not found
 */
export const getAndClearReturnUrl = () => {
  const returnUrl = sessionStorage.getItem("returnUrl");
  if (returnUrl) {
    sessionStorage.removeItem("returnUrl");
  }
  return returnUrl;
};

/**
 * Handles user redirection to login page
 * Clears existing authentication data and redirects to login
 * Saves the current URL to redirect back after successful login
 */
export const redirectToLogin = () => {
  Cookies.remove("accessToken");

  // Save the current URL to redirect back after login
  const currentUrl = window.location.pathname + window.location.search;

  // Only save the URL if it's not already the auth page to avoid loops
  if (currentUrl !== "/auth") {
    // Store the return URL in sessionStorage for security
    sessionStorage.setItem("returnUrl", currentUrl);
  }

  window.location.href = "/auth";
};

/**
 * Handles user redirection to permission denied page
 * @param {string} ErrorMessage - Error message to be logged
 */
export const redirectToUserHasNoAccess = (ErrorMessage) => {
  window.location.href = "/permissionDenied";
  console.log(ErrorMessage);
};
